import type { BullhornAuthData } from './bullhornService';
import {
  fetchBullhornClientCorporationsRaw,
  fetchBullhornClientContactsRaw,
  fetchBullhornJobOrdersRaw,
  fetchBullhornJobSubmissions,
  fetchBullhornPlacementsRaw,
  importCandidatesFromBullhorn,
} from './bullhornService';
import { supabase } from './supabaseService';
import {
  supabase_get_bullhorn_clients,
  supabase_sync_bullhorn_clients,
  supabase_sync_bullhorn_contacts,
  supabase_sync_bullhorn_vacancies,
  supabase_sync_bullhorn_job_submissions,
  supabase_upsert_imported_candidates,
  supabase_sync_bullhorn_placements,
  supabase_backfill_bullhorn_application_stage_history,
} from './supabaseService';
import {
  BULLHORN_SYNC_ENTITY_ORDER,
  bhDateLastModified,
  bhLuceneQuery,
  bhSqlWhere,
  recordDisplayName,
  syncSinceMs,
  type BullhornSyncEntityType,
  type BullhornSyncState,
} from './bullhornSyncQueries';
import {
  classifyBullhornRecord,
  loadLocalBullhornIdSet,
  loadLocalOnlyRecords,
} from './bullhornSyncDiff';
import {
  appendSyncLogLine,
  createSyncRun,
  finishSyncRun,
  getRunChanges,
  insertSyncChanges,
  loadBullhornSyncState,
  loadExclusionSet,
  markChangeSynced,
  saveBullhornSyncState,
  type LogLevel,
  type SyncRunMode,
} from './bullhornSyncStore';

export type SyncProgressCallback = (ev: {
  phase: 'discover' | 'apply' | 'done';
  entity?: BullhornSyncEntityType;
  message: string;
  level?: LogLevel;
}) => void;

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

function chunkArray<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size));
}

export async function runBullhornDiscover(params: {
  clientId: string;
  userId?: string;
  auth: BullhornAuthData;
  mode: SyncRunMode;
  onProgress?: SyncProgressCallback;
}): Promise<{ runId: string; changeCount: number; awaitingApply: boolean }> {
  const { clientId, userId, auth, mode, onProgress } = params;
  const emit = (message: string, level: LogLevel = 'info', entity?: BullhornSyncEntityType) => {
    onProgress?.({ phase: 'discover', entity, message, level });
  };

  const syncState = await loadBullhornSyncState(clientId);
  const sinceMs = mode === 'incremental' ? syncSinceMs(syncState.lastSyncAt) : null;
  const exclusions = await loadExclusionSet(clientId);

  const run = await createSyncRun({
    clientId,
    userId,
    mode,
    entities: BULLHORN_SYNC_ENTITY_ORDER,
  });

  const log = async (
    entityType: string,
    level: LogLevel,
    message: string,
    bullhornId?: string,
    metadata?: Record<string, unknown>
  ) => {
    await appendSyncLogLine({
      runId: run.id,
      clientId,
      entityType,
      level,
      message,
      bullhornId,
      metadata,
    });
    emit(message, level, entityType as BullhornSyncEntityType);
  };

  await log('system', 'info', mode === 'full' ? 'Start volledige synchronisatie (alle data)' : `Start incrementele sync (wijzigingen sinds ${syncState.lastSyncAt || 'onbekend'})`);

  let totalDiscovered = 0;
  let totalExcluded = 0;
  const changeRows: Parameters<typeof insertSyncChanges>[0] = [];

  const localIdCache = new Map<BullhornSyncEntityType, Set<string>>();

  const getLocalBhIds = async (entity: BullhornSyncEntityType): Promise<Set<string>> => {
    if (!localIdCache.has(entity)) {
      localIdCache.set(entity, await loadLocalBullhornIdSet(clientId, entity));
    }
    return localIdCache.get(entity)!;
  };

  const addRecords = async (entity: BullhornSyncEntityType, records: Record<string, unknown>[]) => {
    const localBhIds = await getLocalBhIds(entity);
    const bhIdsSeen = new Set<string>();
    let newInBh = 0;
    let updates = 0;

    for (const row of records) {
      const bhId = String(row.id ?? '').trim();
      if (!bhId) continue;
      bhIdsSeen.add(bhId);
      const { origin, action } = classifyBullhornRecord(bhId, localBhIds);
      if (origin === 'new_in_bullhorn') newInBh++;
      else updates++;

      const key = `${entity}:${bhId}`;
      const excluded = exclusions.has(key);
      if (excluded) totalExcluded++;
      changeRows.push({
        runId: run.id,
        clientId,
        entityType: entity,
        bullhornId: bhId,
        displayName: recordDisplayName(entity, row),
        action,
        dateLastModified: bhDateLastModified(row),
        metadata: { status: row.status, origin },
        excluded,
      });
      totalDiscovered++;
    }

    if (mode === 'full') {
      const localOnly = await loadLocalOnlyRecords(clientId, entity, bhIdsSeen);
      for (const loc of localOnly) {
        const pseudoId = `local:${loc.localId}`;
        const key = `${entity}:${pseudoId}`;
        const excluded = exclusions.has(key);
        if (excluded) totalExcluded++;
        changeRows.push({
          runId: run.id,
          clientId,
          entityType: entity,
          bullhornId: pseudoId,
          displayName: loc.displayName,
          action: 'create',
          metadata: { origin: 'new_in_werftools', localId: loc.localId },
          excluded,
        });
        totalDiscovered++;
      }
      if (localOnly.length > 0) {
        await log(
          entity,
          'info',
          `${records.length} in Bullhorn (${newInBh} nieuw, ${updates} bestaand) · ${localOnly.length} alleen in Werftools`
        );
        return;
      }
    }

    await log(
      entity,
      'info',
      `${records.length} in Bullhorn — ${newInBh} nieuw in Bullhorn, ${updates} wijziging in beide systemen`
    );
  };

  try {
    // CRM companies
    const corpQuery = bhSqlWhere('id>0', sinceMs);
    const corps = await fetchBullhornClientCorporationsRaw(auth, corpQuery, true, (m) =>
      void log('crm_company', 'pending', m)
    );
    await addRecords('crm_company', (corps.data || []) as Record<string, unknown>[]);

    // CRM contacts
    const contactQuery = bhSqlWhere('id>0', sinceMs);
    const contacts = await fetchBullhornClientContactsRaw(auth, contactQuery, true, (m) =>
      void log('crm_contact', 'pending', m)
    );
    await addRecords('crm_contact', (contacts.data || []) as Record<string, unknown>[]);

    // Vacancies
    const vacQuery = bhLuceneQuery('isDeleted:0', sinceMs);
    const vacs = await fetchBullhornJobOrdersRaw(auth, vacQuery, true, true, (m) =>
      void log('vacancy', 'pending', m)
    );
    await addRecords('vacancy', (vacs.data || []) as Record<string, unknown>[]);

    // Candidates
    const candQuery = bhLuceneQuery('isDeleted:0', sinceMs);
    const cands = await importCandidatesFromBullhorn(
      auth,
      candQuery,
      'id,firstName,lastName,email,phone,mobile,status,dateAdded,dateLastModified',
      200,
      true,
      (m) => void log('candidate', 'pending', m)
    );
    await addRecords('candidate', (cands.data || []) as Record<string, unknown>[]);

    // Placements
    const placeQuery = bhSqlWhere('id>0', sinceMs);
    const placements = await fetchBullhornPlacementsRaw(auth, placeQuery, true, (m) =>
      void log('placement', 'pending', m)
    );
    await addRecords('placement', (placements.data || []) as Record<string, unknown>[]);

    // Pipelines: discover via modified vacancies (incremental) or all open vacancies (full)
    const vacBhIds = (vacs.data || []).map((v: { id: unknown }) => String(v.id).trim()).filter(Boolean);
    if (vacBhIds.length === 0 && mode === 'incremental') {
      await log('pipeline', 'info', 'Geen gewijzigde vacatures — pipelines overgeslagen');
    } else {
      const pipelineVacIds =
        mode === 'full'
          ? (
              await fetchBullhornJobOrdersRaw(auth, 'isDeleted:0', true, true, () => {})
            ).data?.map((v: { id: unknown }) => String(v.id).trim()) || []
          : vacBhIds;

      let pipelineCount = 0;
      const pipelineLocalBhIds = await getLocalBhIds('pipeline');
      for (const chunk of chunkArray(pipelineVacIds, 10)) {
        await Promise.all(
          chunk.map(async (bhVacId) => {
            try {
              const subs = await fetchBullhornJobSubmissions(auth, bhVacId);
              for (const sub of subs || []) {
                const row = sub as Record<string, unknown>;
                const bhId = String(row.id ?? '').trim();
                if (!bhId) continue;
                const entity: BullhornSyncEntityType = 'pipeline';
                const key = `${entity}:${bhId}`;
                const excluded = exclusions.has(key);
                if (excluded) totalExcluded++;
                const { origin, action } = classifyBullhornRecord(bhId, pipelineLocalBhIds);
                changeRows.push({
                  runId: run.id,
                  clientId,
                  entityType: entity,
                  bullhornId: bhId,
                  displayName: recordDisplayName(entity, row),
                  action,
                  dateLastModified: bhDateLastModified(row),
                  metadata: { vacancyBhId: bhVacId, status: row.status, origin },
                  excluded,
                });
                pipelineCount++;
                totalDiscovered++;
              }
            } catch {
              /* per-vacancy errors logged on apply */
            }
          })
        );
        await log('pipeline', 'pending', `Pipelines scannen… (${Math.min(pipelineCount, pipelineVacIds.length * 20)} submissions)`);
        await delay(200);
      }
      await log('pipeline', 'info', `${pipelineCount} pipeline-record(s) gevonden`);
    }

    // Pipeline history marker rows (backfill processes all linked apps)
    changeRows.push({
      runId: run.id,
      clientId,
      entityType: 'pipeline_history',
      bullhornId: '_backfill_',
      displayName: 'Pipeline-geschiedenis (JobSubmission history)',
      action: 'upsert',
      excluded: exclusions.has('pipeline_history:_backfill_'),
    });
    totalDiscovered++;

    await insertSyncChanges(changeRows);

    await finishSyncRun(run.id, {
      status: 'running',
      total_discovered: totalDiscovered,
      total_excluded: totalExcluded,
      summary: { awaitingApply: mode === 'incremental', discoverCompletedAt: new Date().toISOString() },
    });

    if (mode === 'full') {
      await runBullhornApply({
        runId: run.id,
        clientId,
        userId,
        auth,
        onProgress: (ev) => onProgress?.({ ...ev, phase: ev.phase === 'done' ? 'done' : 'apply' }),
      });
      return { runId: run.id, changeCount: totalDiscovered, awaitingApply: false };
    }

    await log('system', 'success', `Ontdekking voltooid: ${totalDiscovered} wijziging(en). Controleer de tabel en klik op Uitvoeren.`);
    return { runId: run.id, changeCount: totalDiscovered, awaitingApply: true };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    await log('system', 'error', `Ontdekking mislukt: ${msg}`);
    await finishSyncRun(run.id, { status: 'error', total_discovered: totalDiscovered, summary: { error: msg } });
    throw e;
  }
}

export async function runBullhornApply(params: {
  runId: string;
  clientId: string;
  userId?: string;
  auth: BullhornAuthData;
  onProgress?: SyncProgressCallback;
}): Promise<void> {
  const { runId, clientId, userId, auth, onProgress } = params;
  const changes = await getRunChanges(runId);
  const pending = changes.filter(
    (c) => !c.excluded && c.status === 'pending' && !String(c.bullhorn_id).startsWith('local:')
  );

  const log = async (
    entityType: string,
    level: LogLevel,
    message: string,
    bullhornId?: string
  ) => {
    await appendSyncLogLine({ runId, clientId, entityType, level, message, bullhornId });
    onProgress?.({
      phase: 'apply',
      entity: entityType as BullhornSyncEntityType,
      message,
      level,
    });
  };

  let synced = 0;
  let errors = 0;

  const byEntity = (type: BullhornSyncEntityType) =>
    pending.filter((c) => c.entity_type === type && c.bullhorn_id !== '_backfill_');

  try {
    // CRM companies
    const corpIds = new Set(byEntity('crm_company').map((c) => c.bullhorn_id));
    if (corpIds.size > 0) {
      await log('crm_company', 'info', `Synchroniseren van ${corpIds.size} bedrijf(en)…`);
      const corps = await fetchBullhornClientCorporationsRaw(auth, 'id>0', true);
      const toSync = (corps.data || []).filter((r: { id: unknown }) => corpIds.has(String(r.id)));
      await supabase_sync_bullhorn_clients(clientId, toSync, (m) => void log('crm_company', 'pending', m));
      for (const c of byEntity('crm_company')) {
        await markChangeSynced(c.id);
        await log('crm_company', 'success', `Gesynchroniseerd: ${c.display_name}`, c.bullhorn_id);
        synced++;
      }
    }

    // CRM contacts
    const contactIds = new Set(byEntity('crm_contact').map((c) => c.bullhorn_id));
    if (contactIds.size > 0) {
      await log('crm_contact', 'info', `Synchroniseren van ${contactIds.size} contact(en)…`);
      const contacts = await fetchBullhornClientContactsRaw(auth, 'id>0', true);
      const toSync = (contacts.data || []).filter((r: { id: unknown }) => contactIds.has(String(r.id)));
      await supabase_sync_bullhorn_contacts(clientId, toSync, (m) => void log('crm_contact', 'pending', m));
      for (const c of byEntity('crm_contact')) {
        await markChangeSynced(c.id);
        await log('crm_contact', 'success', `Gesynchroniseerd: ${c.display_name}`, c.bullhorn_id);
        synced++;
      }
    }

    // Vacancies
    const vacIds = new Set(byEntity('vacancy').map((c) => c.bullhorn_id));
    if (vacIds.size > 0) {
      const mappedClients = await supabase_get_bullhorn_clients(clientId);
      if (!mappedClients?.length) {
        throw new Error('Synchroniseer eerst CRM (bedrijven) voordat je vacatures importeert.');
      }
      await log('vacancy', 'info', `Synchroniseren van ${vacIds.size} vacature(s)…`);
      const vacs = await fetchBullhornJobOrdersRaw(auth, 'isDeleted:0', true, true);
      const toSync = (vacs.data || []).filter((r: { id: unknown }) => vacIds.has(String(r.id)));
      const res = await supabase_sync_bullhorn_vacancies(clientId, toSync, mappedClients, (m) =>
        void log('vacancy', 'pending', m)
      );
      await log('vacancy', 'success', `Vacatures verwerkt: ${res.count}`);
      for (const c of byEntity('vacancy')) {
        await markChangeSynced(c.id);
        synced++;
      }
    }

    // Candidates
    const candIds = new Set(byEntity('candidate').map((c) => c.bullhorn_id));
    if (candIds.size > 0) {
      await log('candidate', 'info', `Synchroniseren van ${candIds.size} kandidaat/kandidaten…`);
      const cands = await importCandidatesFromBullhorn(auth, 'isDeleted:0', undefined, 200, true);
      const toSync = (cands.data || []).filter((r: { id: unknown }) => candIds.has(String(r.id)));
      const res = await supabase_upsert_imported_candidates(clientId, toSync, (m) => void log('candidate', 'pending', m));
      await log('candidate', 'success', `Kandidaten: ${res.imported} geïmporteerd, ${res.errors} fouten`);
      for (const c of byEntity('candidate')) {
        await markChangeSynced(c.id);
        synced++;
      }
    }

    // Placements
    const placeIds = new Set(byEntity('placement').map((c) => c.bullhorn_id));
    if (placeIds.size > 0) {
      await log('placement', 'info', `Synchroniseren van ${placeIds.size} plaatsing(en)…`);
      const placements = await fetchBullhornPlacementsRaw(auth, 'id>0', true);
      const toSync = (placements.data || []).filter((r: { id: unknown }) => placeIds.has(String(r.id)));
      const res = await supabase_sync_bullhorn_placements(clientId, toSync);
      await log('placement', 'success', `Plaatsingen: ${res.count} verwerkt`);
      for (const c of byEntity('placement')) {
        await markChangeSynced(c.id);
        synced++;
      }
    }

    // Pipelines
    const pipelineChanges = byEntity('pipeline');
    if (pipelineChanges.length > 0) {
      const vacBhIds = [
        ...new Set(
          pipelineChanges
            .map((c) => (c.metadata as { vacancyBhId?: string })?.vacancyBhId)
            .filter(Boolean) as string[]
        ),
      ];
      const { data: localVacs } = await supabase
        .from('vacancies')
        .select('id, bullhorn_id')
        .in('bullhorn_id', vacBhIds.length ? vacBhIds : ['__none__']);
      const localMap = new Map((localVacs || []).map((v) => [String(v.bullhorn_id).trim(), v.id]));

      for (const vacBhId of vacBhIds) {
        const localVacId = localMap.get(vacBhId);
        if (!localVacId) continue;
        try {
          const subs = await fetchBullhornJobSubmissions(auth, vacBhId);
          const subIds = new Set(
            pipelineChanges.filter((c) => (c.metadata as { vacancyBhId?: string })?.vacancyBhId === vacBhId).map((c) => c.bullhorn_id)
          );
          const toApply = (subs || []).filter((s: { id: unknown }) => subIds.has(String(s.id)));
          if (toApply.length) {
            await supabase_sync_bullhorn_job_submissions(clientId, String(localVacId), toApply);
            await log('pipeline', 'success', `Vacature BH ${vacBhId}: ${toApply.length} submission(s) gesynchroniseerd`, vacBhId);
          }
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : String(e);
          await log('pipeline', 'error', `Vacature BH ${vacBhId}: ${msg}`, vacBhId);
          errors++;
        }
      }
      for (const c of pipelineChanges) {
        await markChangeSynced(c.id);
        synced++;
      }
    }

    // Pipeline history backfill
    if (pending.some((c) => c.entity_type === 'pipeline_history')) {
      await log('pipeline_history', 'info', 'Pipeline-geschiedenis importeren uit Bullhorn JobSubmission history…');
      const result = await supabase_backfill_bullhorn_application_stage_history(clientId, auth, {
        onProgress: (m) => void log('pipeline_history', 'pending', m),
      });
      await log(
        'pipeline_history',
        'success',
        `Geschiedenis: ${result.inserted} events toegevoegd, ${result.skipped} overgeslagen, ${result.warnings.length} waarschuwingen`
      );
      const histChange = pending.find((c) => c.entity_type === 'pipeline_history');
      if (histChange) await markChangeSynced(histChange.id);
      synced++;
    }

    const syncState: BullhornSyncState = {
      initialFullSyncCompleted: true,
      lastSyncAt: new Date().toISOString(),
      entityLastSyncAt: Object.fromEntries(
        BULLHORN_SYNC_ENTITY_ORDER.map((e) => [e, new Date().toISOString()])
      ) as BullhornSyncState['entityLastSyncAt'],
    };
    await saveBullhornSyncState(clientId, syncState);

    const status = errors > 0 ? 'partial' : 'success';
    await finishSyncRun(runId, {
      status,
      total_synced: synced,
      total_errors: errors,
      summary: { completedAt: new Date().toISOString() },
    });
    await log('system', 'success', `Synchronisatie afgerond: ${synced} record(s), ${errors} fout(en).`);
    onProgress?.({ phase: 'done', message: 'Klaar', level: 'success' });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    await log('system', 'error', `Synchronisatie mislukt: ${msg}`);
    await finishSyncRun(runId, { status: 'error', total_errors: errors + 1, summary: { error: msg } });
    throw e;
  }
}
